import styled, { css } from 'styled-components';
import { tokens } from '@companycam/slab';
import { lighten, darken } from 'polished';

export const avatarSizes = {
  large: 40,
  medium: 32,
  small: 24,
  tiny: 16,
};

export const systemFontFamily = `font-family: system-ui, -apple-system, BlinkMacSystemFont,
'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;`;

export const headingFontFamily = `font-family: 'Averta', system-ui, -apple-system, BlinkMacSystemFont,
'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;`;

/*
  Start InlineInput and InlineTextArea shared styles
*/
export const INLINE_HIGHLIGHT_BG_COLOR = lighten(
  0.5,
  tokens.color_link_primary_le,
);
export const INLINE_TEXT_HOVER_COLOR = darken(
  0.25,
  tokens.color_text_default_le,
);

export const HiddenInlineLabel = styled.label`
  all: initial;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const InvisibleText = styled.span`
  opacity: 0;
  min-width: 1px;
`;

const getFontSize = (fontSize) => {
  if (typeof fontSize === 'string') {
    return fontSize;
  } else if (typeof fontSize === 'number') {
    return `${fontSize}px`;
  } else {
    return 'inherit';
  }
};

export const getTextStyles = (textStyle) => {
  const textStyleColor = textStyle.color || tokens.color_text_default_le;
  const textStyleHoverColor =
    darken(0.125, textStyleColor) || INLINE_TEXT_HOVER_COLOR;

  return `
    font-family: ${
      textStyle.fontFamily
        ? textStyle.fontFamily
        : 'system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,sans-serif'
    };
    font-size: ${getFontSize(textStyle.fontSize)};
    font-weight: ${textStyle.fontWeight ? textStyle.fontWeight : 'normal'};
    color: ${textStyleColor};

    &:hover {
      color: ${textStyleHoverColor};
    }
    `;
};

export const getFocusLineStyles = (isHovered, isFocused) => {
  return `
    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: ${tokens.size_border_width_m}px;
      background: var(--cc_color_border_input_active);
      transition: all 0.2s ease-out;
      transform: scale(${isFocused || isHovered ? '1' : '0.25'});
      opacity: ${isFocused || isHovered ? '1' : '0'};
    }
    `;
};

export const sharedInputStyles = `
  all: initial;
  cursor: text;
  box-sizing: border-box;
  display: block;
  background: transparent;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &::selection {
    background: ${INLINE_HIGHLIGHT_BG_COLOR};
  }

  &::placeholder {
    color: ${tokens.color_link_subtle_le};
  }
`;

export const sharedTextOverlayStyles = `
  pointer-events: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
/*
  End InlineInput and InlineTextArea shared styles
*/

/*
 *
 * Start Button shared styles
 *
 */
export const focusStyles = css`
  outline: var(--cc_size_border_width_m) solid
    var(--cc_color_border_input_active);
  outline-offset: 0.25ch;
`;

export const baseButtonStyles = ({ disabled = false }) => {
  return `
    all: initial;
    box-sizing: border-box;
    font-family: inherit;
    font-weight: 700;
    max-width: 100%;
    overflow: visible;
    display: inline-block;
    vertical-align: middle;
    text-align: center;
    background: none;
    position: relative;
    appearance: none;
    text-decoration: none;
    touch-action: manipulation;
    cursor: pointer;
    user-select: none;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    &:focus-visible {
      ${focusStyles}
    }
    
    ${
      disabled
        ? css`
            pointer-events: none;
          `
        : ''
    }
    ${
      disabled
        ? css`
            opacity: 0.5;
            cursor: not-allowed;
          `
        : ''
    }
  `;
};

export const buttonSize = {
  tiny: 'var(--cc_size_avatar_s)',
  small: 'var(--cc_size_avatar_m)',
  medium: 'var(--cc_size_avatar_l)',
  large: '48px',
};

/*
 *
 * End Button shared styles
 *
 */
